import { useSettings } from '@/data/Settings';
import { usePathname } from 'next/navigation';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { useEffect, useMemo, useRef } from 'react';
import {
	ADOBE_NOT_ALLOWED_PATH_LIST_KEY,
	ADOBE_TARGET_PAGE_VIEWS_ENABLED_KEY,
	ENABLE_ADOBE_SCRIPT_FLAG,
} from 'shared/custom/constants/commonConstants';
import { isScriptAllowedForPath } from 'shared/custom/utils/scriptPathFilter';
import { sendAdobeTargetView } from '../utils';
import { resolveViewMapping, resolveViewName } from './utils';

/**
 * Fires an Adobe Target view (WebSDK `sendEvent` with a `viewName`) whenever
 * the pathname changes and resolves to a mapped view.
 *
 * Storeconf: set userData `ADOBE_TARGET_PAGE_VIEWS_ENABLED` to `'true'` to
 * enable this hook. Disabled by default while the value doesn't exist. Also
 * short-circuits globally when `ENABLE_ADOBE_SCRIPT_FLAG` isn't `'true'`.
 */
export const useAdobeTargetPageView = () => {
	const pathname = usePathname();
	const trackException = useTrackException();
	const { settings } = useSettings();
	const { userData } = settings;

	const mappings = useMemo(() => resolveViewMapping(userData ?? {}), [userData]);
	const prevPathRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		try {
			const isAdobeScriptEnabled = userData?.[ENABLE_ADOBE_SCRIPT_FLAG] === 'true';
			if (!isAdobeScriptEnabled) return;

			if (!pathname) return;
			if (pathname === prevPathRef.current) return;

			const isEnabled = userData?.[ADOBE_TARGET_PAGE_VIEWS_ENABLED_KEY] === 'true';
			if (!isEnabled) return;

			const isAllowed = isScriptAllowedForPath(userData, pathname, ADOBE_NOT_ALLOWED_PATH_LIST_KEY);
			if (!isAllowed) return;

			const viewName = resolveViewName({ pathname, mappings });

			if (viewName === undefined) return;

			prevPathRef.current = pathname;
			sendAdobeTargetView(viewName);
		} catch (e) {
			console.error('Error sending Adobe Target view', e);
			trackException(e);
		}
	}, [pathname, userData, mappings, trackException]);
};
